// src/components/hooks/useSessionLoader.ts
import { useEffect, useCallback } from 'react';
import { useSession } from './useSession';
import { useSharedSession } from './useSharedSession';
import { VideoState, SegmentState } from '../types';
import {
  getInvalidShareSessionMessage,
  resolveShareSessionRoute,
  stripShareSessionParams
} from '../../utils/shareSessionUrl';
import { getCurrentPlatformCapabilities } from '../../platform/platformCapabilities';

interface UseSessionLoaderProps {
  setVideoState: React.Dispatch<React.SetStateAction<VideoState>>;
  setSegmentState: React.Dispatch<React.SetStateAction<SegmentState>>;
  setIsDemo: (isDemo: boolean) => void;
  setOriginalVideoFile: (file: File | null) => void;
}

/**
 * Hook to handle loading sessions (both regular and shared) on mount
 * Encapsulates all the session loading logic to avoid ESLint warnings
 */
export const useSessionLoader = ({
  setVideoState,
  setSegmentState,
  setIsDemo,
  setOriginalVideoFile
}: UseSessionLoaderProps) => {
  const {
    handleLoadSessionFromUrl,
    markSessionLoadAttempted,
    hasSessionLoadAttempted
  } = useSession();

  const {
    loadSharedSessionFromUrl,
    markShareLoadAttempted,
    hasShareLoadAttempted
  } = useSharedSession();
  const sharingEnabled = getCurrentPlatformCapabilities().canExposeSharing;

  // Create stable callback functions that won't change on re-renders
  const videoStateUpdater = useCallback((updates: Partial<VideoState>) => {
    setVideoState(prev => ({ ...prev, ...updates }));
  }, [setVideoState]);

  const segmentStateUpdater = useCallback((updates: Partial<SegmentState>) => {
    setSegmentState(prev => ({ ...prev, ...updates }));
  }, [setSegmentState]);

  const demoUpdater = useCallback((updates: { isDemo?: boolean }) => {
    if (updates.isDemo !== undefined) setIsDemo(updates.isDemo);
  }, [setIsDemo]);

  useEffect(() => {
    const loadSessionOnMount = async () => {
      const route = resolveShareSessionRoute(window.location.search);

      if (!sharingEnabled && route.kind !== 'none') {
        window.history.replaceState(
          {},
          document.title,
          stripShareSessionParams(window.location.href)
        );
        return;
      }

      if (route.kind === 'invalid') {
        window.history.replaceState(
          {},
          document.title,
          stripShareSessionParams(window.location.href)
        );
        alert(getInvalidShareSessionMessage(route));
        return;
      }

      if (route.kind === 'share') {
        await loadSharedSessionFromUrl(
          videoStateUpdater,
          segmentStateUpdater,
          demoUpdater,
          setOriginalVideoFile,
          route.shareId
        );
        return;
      }

      if (route.kind === 'session') {
        await handleLoadSessionFromUrl(
          videoStateUpdater,
          segmentStateUpdater,
          demoUpdater,
          setOriginalVideoFile,
          route.sessionId
        );
      }
    };

    if (!hasSessionLoadAttempted() && !hasShareLoadAttempted()) {
      markSessionLoadAttempted();
      markShareLoadAttempted();
      loadSessionOnMount();
    }
  }, [
    hasSessionLoadAttempted,
    hasShareLoadAttempted,
    markSessionLoadAttempted,
    markShareLoadAttempted,
    handleLoadSessionFromUrl,
    loadSharedSessionFromUrl,
    videoStateUpdater,
    segmentStateUpdater,
    demoUpdater,
    setOriginalVideoFile,
    sharingEnabled
  ]);
};
